import { COLORS, CONFIG } from "../config";
import type { Engine } from "../engine/engine";
import { noteTailGapSeconds } from "../note-gap";
import type { DisplayOptions } from "../store/prefs";
import type { Hand, KeyState, NoteEvent } from "../types";
import { drillWindowNotes } from "./drill-window";
import { buildKeyboard, isBlackKey, type Keyboard, noteLetter } from "./keyboard";
import { staffFrame } from "./staff";
import { drawStaff } from "./staff-draw";
import { computeStageRects, keyboardBandHeight } from "./stage-layout";

// Renderer. Each frame reads engine state and draws the
// highway, falling notes, and the on-screen keyboard with per-key feedback.

const NOTE_COLORS: Record<Hand, string> = {
  left: COLORS.noteLeft,
  right: COLORS.noteRight,
  unknown: COLORS.noteUnknown,
};

const KEY_STATE_COLORS: Record<KeyState, string> = {
  upcoming: COLORS.keyPressed, // blue: a song note is approaching this key
  expected: COLORS.keyExpected,
  correct: COLORS.keyCorrect,
  wrong: COLORS.keyWrong,
};

/** Collapse the "auto" follow setting (1 row → Flow scroll, else Page sweep). */
function resolveFollow(follow: string, rows: number): "flow" | "page" {
  if (follow === "flow") return "flow";
  if (follow === "page") return "page";
  return rows === 1 ? "flow" : "page";
}

// Keyboard geometry only changes on resize / calibration, not per frame —
// cache it so the render loop doesn't allocate 88 key objects every frame
// (the main source of GC pressure / periodic hitches).
let cachedKeyboard: { width: number; ww: number; off: number; kb: Keyboard } | null = null;

function keyboardFor(width: number, cal: { whiteWidthPx: number; offsetPx: number }): Keyboard {
  const c = cachedKeyboard;
  if (c && c.width === width && c.ww === cal.whiteWidthPx && c.off === cal.offsetPx) return c.kb;
  const kb = buildKeyboard(width, cal);
  cachedKeyboard = { width, ww: cal.whiteWidthPx, off: cal.offsetPx, kb };
  return kb;
}

// Falling-note tail gradients, cached by (color, rounded height). A note's height is
// constant as it falls (it depends on duration + highway span, not the playhead) — only its
// y-position moves — so a gradient built at the origin is reused frame-to-frame via translate,
// instead of allocating createLinearGradient + 3 stops per note per frame (the old hot path).
// The cache is cleared when the highway span changes (height scales with it).
const gradCache = new Map<string, CanvasGradient>();
let gradCacheSpan = -1;

function noteGradient(ctx: CanvasRenderingContext2D, color: string, h: number): CanvasGradient {
  const hb = Math.max(3, Math.round(h));
  const key = `${color}|${hb}`;
  const cached = gradCache.get(key);
  if (cached) return cached;
  const grad = ctx.createLinearGradient(0, 0, 0, hb);
  const fadeFrac = Math.min(0.5, CONFIG.noteFadePx / hb);
  grad.addColorStop(0, `${color}00`); // transparent (6-digit hex → +alpha 00)
  grad.addColorStop(fadeFrac, color);
  grad.addColorStop(1, color);
  gradCache.set(key, grad);
  return grad;
}

export function render(
  ctx: CanvasRenderingContext2D,
  engine: Engine,
  cal: { whiteWidthPx: number; offsetPx: number },
  held: ReadonlySet<number>,
  width: number,
  height: number,
  display: DisplayOptions,
  commandLabels: ReadonlyMap<number, string> = NO_LABELS,
  keyHint?: string,
  chords?: readonly (string | null)[],
): Keyboard {
  const kb = keyboardFor(width, cal);
  const rects = computeStageRects(width, height, {
    staffEnabled: display.staffEnabled,
    staffSplit: display.staffSplit,
    flow: resolveFollow(display.staffFollow, display.staffRows) === "flow",
    whiteWidth: cal.whiteWidthPx,
  });
  const keyboardTop = rects.keyboardTop;
  const highwayTop = rects.highwayTop;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  if (display.staffEnabled && rects.staffHeight > 0) {
    drawStaff(
      ctx,
      staffFrame({
        song: engine.song,
        // Coach drilling: show only the active window's notes (drill-window.ts).
        notes: drillWindowNotes(engine.notes, engine.windowStart, engine.windowEnd),
        playhead: engine.playhead,
        judgement: (n) => engine.judgementFor(n),
        recentWrong: engine.recentWrong(),
        rect: rects,
        width,
        rows: display.staffRows,
        follow: resolveFollow(display.staffFollow, display.staffRows),
        keyHint,
        chords: display.showChordSymbols ? chords : undefined,
      }),
    );
  }

  drawGuides(ctx, kb, keyboardTop, highwayTop);
  drawFallingNotes(
    ctx,
    engine,
    kb,
    width,
    keyboardTop,
    highwayTop,
    display.noteLabels,
    display.fingerLabels,
    display.highwayOpacity,
  );

  ctx.strokeStyle = COLORS.hitLine;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, keyboardTop);
  ctx.lineTo(width, keyboardTop);
  ctx.stroke();

  if (display.showKeyboard)
    drawKeyboard(ctx, kb, engine.keyStates(held), held, engine.playing, keyboardTop, height);
  if (display.slotLabels) drawSlotLabels(ctx, kb, keyboardTop, height, display.showKeyboard);
  if (engine.mode === "scored") drawScoreHud(ctx, engine);
  else if (engine.mode === "wait") drawWaitHud(ctx, engine);
  drawKeyGlyphs(ctx, kb, keyboardTop, commandLabels);
  return kb;
}

/**
 * Draw the idle screen (no song loaded): background, hit line, and the on-screen
 * keyboard with currently-held keys highlighted — so a player can confirm MIDI
 * input is reaching the app before loading anything.
 */
export function renderIdle(
  ctx: CanvasRenderingContext2D,
  cal: { whiteWidthPx: number; offsetPx: number },
  held: ReadonlySet<number>,
  width: number,
  height: number,
  display: DisplayOptions,
  commandLabels: ReadonlyMap<number, string> = NO_LABELS,
): void {
  const kb = keyboardFor(width, cal);
  const keyboardTop = height - keyboardBandHeight(height, cal.whiteWidthPx);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);
  drawGuides(ctx, kb, keyboardTop, 0);
  ctx.strokeStyle = COLORS.hitLine;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, keyboardTop);
  ctx.lineTo(width, keyboardTop);
  ctx.stroke();
  if (display.showKeyboard) drawKeyboard(ctx, kb, EMPTY_STATES, held, false, keyboardTop, height);
  if (display.slotLabels) drawSlotLabels(ctx, kb, keyboardTop, height, display.showKeyboard);
  drawKeyGlyphs(ctx, kb, keyboardTop, commandLabels);
}

const EMPTY_STATES: Map<number, KeyState> = new Map();
const NO_LABELS: ReadonlyMap<number, string> = new Map();

/** Draw command-mode glyphs (←/→/●/✕…) on their keys, near the key tops. */
function drawKeyGlyphs(
  ctx: CanvasRenderingContext2D,
  kb: Keyboard,
  keyboardTop: number,
  labels: ReadonlyMap<number, string>,
): void {
  if (labels.size === 0) return;
  ctx.save();
  ctx.font = "bold 14px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 3;
  for (const [pitch, glyph] of labels) {
    const x = kb.xCenter(pitch);
    const y = keyboardTop + 24;
    ctx.strokeStyle = COLORS.bg;
    ctx.fillStyle = COLORS.keyPressed;
    ctx.strokeText(glyph, x, y);
    ctx.fillText(glyph, x, y);
  }
  ctx.restore();
}

function drawGuides(
  ctx: CanvasRenderingContext2D,
  kb: Keyboard,
  keyboardTop: number,
  highwayTop: number,
): void {
  ctx.strokeStyle = COLORS.guide;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const key of kb.keys) {
    if (key.isBlack) continue;
    ctx.moveTo(key.x, highwayTop);
    ctx.lineTo(key.x, keyboardTop);
  }
  ctx.stroke();
}

function drawFallingNotes(
  ctx: CanvasRenderingContext2D,
  engine: Engine,
  kb: Keyboard,
  width: number,
  keyboardTop: number,
  highwayTop: number,
  noteLabels: boolean,
  fingerLabels: boolean,
  highwayOpacity: number,
): void {
  if (highwayOpacity <= 0) return; // reading: highway fully faded, staff only
  const look = CONFIG.lookaheadSeconds;
  const span = keyboardTop - highwayTop;
  if (span !== gradCacheSpan) {
    gradCache.clear(); // note heights scale with the span — stale gradients no longer apply
    gradCacheSpan = span;
  }
  // Clip to the highway so notes that pass the hit line in scored mode slide
  // under it (rather than drawing over the keyboard or up into the staff).
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, highwayTop, width, span);
  ctx.clip();
  // The reading crutch fade: dim the whole highway (notes + labels) without
  // touching the staff or keyboard. `ctx.restore()` below resets it.
  ctx.globalAlpha = highwayOpacity;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Coach drilling: only the active window's notes fall (drill-window.ts) — notes beyond
  // the looping window would otherwise approach the hit line and never be playable.
  for (const note of drillWindowNotes(engine.notes, engine.windowStart, engine.windowEnd)) {
    const untilOnset = note.onset - engine.playhead;
    // Notes are onset-ordered: once one is above the lookahead horizon, so is everything
    // after it — stop scanning rather than walking the whole song every frame.
    if (untilOnset > look) break;
    if (note.onset + note.duration < engine.playhead) continue; // already fully past the line

    const cx = kb.xCenter(note.midi);
    if (cx < 0 || cx > width) continue; // out of covered range — drop

    // Trim the trailing (release) edge by the display gap so stacked same-pitch
    // notes read as separate blocks; the leading (onset/press) edge stays exact.
    const drawnDur = note.duration - noteTailGapSeconds(note.duration);
    const yBottom = highwayTop + span * (1 - untilOnset / look);
    const yTop = highwayTop + span * (1 - (untilOnset + drawnDur) / look);
    const w = (isBlackKey(note.midi) ? kb.blackWidth : kb.whiteWidth) * 0.86;
    const h = Math.max(3, yBottom - yTop);

    // Solid body with a soft tail: the trailing (top/release) edge fades to transparent so
    // the note's end reads as a tail. The gradient is cached by (color, height) and painted
    // via a translate, so the origin-built gradient lands at the note's y (see noteGradient).
    const color = noteColor(engine, note);
    ctx.translate(0, yTop);
    ctx.fillStyle = noteGradient(ctx, color, h);
    roundRect(ctx, cx - w / 2, 0, w, h, 4);
    ctx.fill();
    ctx.translate(0, -yTop);

    // Bottom-edge label (near the key); the finger number takes this slot, the note
    // letter moves to the top edge when both are on so they don't overlap.
    const bottomY = Math.min(keyboardTop - 8, Math.max(yTop + 8, yBottom - 9));
    const finger = fingerLabels ? engine.fingerOf(note) : undefined;
    if (finger && w >= 11) {
      ctx.font = "700 11px system-ui, sans-serif";
      ctx.fillStyle = "#0b0c0f";
      ctx.fillText(String(finger), cx, bottomY);
    }
    if (noteLabels && w >= 13) {
      ctx.font = "600 11px system-ui, sans-serif";
      ctx.fillStyle = "#0b0c0f";
      ctx.fillText(noteLetter(note.midi), cx, finger ? yTop + 9 : bottomY);
    }
  }
  ctx.restore();
}

function drawSlotLabels(
  ctx: CanvasRenderingContext2D,
  kb: Keyboard,
  keyboardTop: number,
  height: number,
  overKeys: boolean,
): void {
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 12px system-ui, sans-serif";
  // On white keys: dark text near the top of the key. With keys hidden: light
  // text in the (otherwise empty) keyboard band.
  ctx.fillStyle = overKeys ? "#4a4f59" : COLORS.hud;
  const y = overKeys ? keyboardTop + 16 : keyboardTop + (height - keyboardTop) / 2;
  for (const key of kb.keys) {
    if (key.isBlack) continue;
    ctx.fillText(noteLetter(key.midi), key.x + key.width / 2, y);
  }
  ctx.restore();
}

function noteColor(engine: Engine, note: NoteEvent): string {
  const judgement = engine.judgementFor(note);
  if (judgement === "miss") return COLORS.noteMiss;
  if (judgement) return COLORS.noteHit;
  return NOTE_COLORS[note.hand];
}

function drawScoreHud(ctx: CanvasRenderingContext2D, engine: Engine): void {
  const s = engine.scoreStats;
  const hits = s.perfect + s.good;
  const total = hits + s.misses;
  const acc = total > 0 ? Math.round((hits / total) * 100) : 100;
  ctx.fillStyle = COLORS.hud;
  ctx.font = "600 16px system-ui, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(
    `${acc}%  ·  ${hits}/${total} hit  ·  ${s.misses} missed  ·  ${s.wrong} wrong`,
    12,
    12,
  );
  // Expression read-out (always measured, shown when there are hits to grade). The
  // Coach Scorecard decides when these gate; here they only inform.
  if (hits > 0) {
    const e = engine.expressionScores();
    ctx.font = "500 13px system-ui, sans-serif";
    ctx.fillText(
      `dynamics ${Math.round(e.dynamics * 100)}%  ·  articulation ${Math.round(e.articulation * 100)}%`,
      12,
      32,
    );
  }
}

// Wait-mode scoreboard: timing vs each note's keyline arrival (the notes still fall at
// tempo; the playhead only pins when you're late). Hidden until the first gate is played.
function drawWaitHud(ctx: CanvasRenderingContext2D, engine: Engine): void {
  const t = engine.waitTimingStats;
  const gates = t.early + t.onTime + t.late;
  if (gates + t.wrong === 0) return; // nothing played yet
  const pct = gates > 0 ? Math.round((t.onTime / gates) * 100) : 0;
  ctx.fillStyle = COLORS.hud;
  ctx.font = "600 16px system-ui, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(
    `${pct}% on time  ·  ${t.early} early · ${t.late} late  ·  ${t.wrong} wrong`,
    12,
    12,
  );
}

// Key fill: gameplay states win while playing; otherwise a held key shows the
// positive "pressed" highlight (MIDI feedback) instead of the error red.
function keyFill(
  state: KeyState | undefined,
  isHeld: boolean,
  playing: boolean,
  base: string,
): string {
  if (state && (playing || state !== "wrong")) return KEY_STATE_COLORS[state];
  if (isHeld) return COLORS.keyPressed;
  return base;
}

function drawKeyboard(
  ctx: CanvasRenderingContext2D,
  kb: Keyboard,
  states: Map<number, KeyState>,
  held: ReadonlySet<number>,
  playing: boolean,
  top: number,
  height: number,
): void {
  const whiteHeight = height - top;
  const blackHeight = whiteHeight * CONFIG.blackKeyHeightRatio;
  ctx.lineWidth = 1;

  // White keys: per-key fill (colour varies), but accumulate the borders into one path and
  // stroke once — strokeRect-per-key issued 50+ separate stroke() calls every frame.
  ctx.strokeStyle = COLORS.whiteKeyEdge;
  ctx.beginPath();
  for (const key of kb.keys) {
    if (key.isBlack) continue;
    ctx.fillStyle = keyFill(states.get(key.midi), held.has(key.midi), playing, COLORS.whiteKey);
    ctx.fillRect(key.x, top, key.width, whiteHeight);
    ctx.rect(key.x, top, key.width, whiteHeight);
  }
  ctx.stroke();

  ctx.strokeStyle = COLORS.blackKeyEdge;
  ctx.beginPath();
  for (const key of kb.keys) {
    if (!key.isBlack) continue;
    ctx.fillStyle = keyFill(states.get(key.midi), held.has(key.midi), playing, COLORS.blackKey);
    ctx.fillRect(key.x, top, key.width, blackHeight);
    ctx.rect(key.x, top, key.width, blackHeight);
  }
  ctx.stroke();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
}
